import {
  type PushErrorOptions,
  ReactIntegration,
  faro,
  getWebInstrumentations,
  initializeFaro,
} from '@grafana/faro-react';

import config from 'config';
import { isMonitoringAgentOrBot } from 'utils/browser';

export function initRUM(sessionId: string) {
  if (config.DEV) return;
  initializeFaro({
    url: config.FARO_COLLECTOR_ENDPOINT,
    trackWebVitalsAttribution: false,
    trackResources: false,
    // https://grafana.com/docs/grafana-cloud/monitor-applications/frontend-observability/instrument/session-tracking/
    sessionTracking: {
      enabled: true,
      generateSessionId() {
        return sessionId;
      },
      sampler: () => {
        // If it's a known bot, return 0 (0% sample rate)
        if (isMonitoringAgentOrBot()) {
          return 0;
        }
        // Otherwise, use your desired RUM sample rate (e.g., 1 for 100%)
        return 1;
      },
    },
    // Filter out ResizeObserver errors
    // https://gympass.atlassian.net/browse/PAREXP-308
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications.',
    ],
    instrumentations: [
      ...getWebInstrumentations({
        captureConsole: true,
        enablePerformanceInstrumentation: false,
      }),
      new ReactIntegration(),
    ],
    app: {
      name: config.APP_NAME,
      version: config.REACT_APP_VERSION,
      environment: config.PARTNERS_ENVIRONMENT,
    },
  });
}

export function addError(error: Error, options?: PushErrorOptions) {
  if (config.DEV) return;
  faro.api.pushError(error, options);
}

type MetaAttributes = NonNullable<
  Parameters<typeof faro.api.setSession>[0]
>['attributes'];

export function setSessionProperty(attributes: MetaAttributes) {
  if (config.DEV) return;

  const session = faro.api.getSession();

  faro.api.setSession({
    ...session,
    attributes: {
      ...session?.attributes,
      ...attributes,
    },
  });
}

type EventAttributes = NonNullable<Parameters<typeof faro.api.pushEvent>>[1];
export function addAction(name: string, attributes: EventAttributes) {
  if (config.DEV) return;
  faro.api.pushEvent(name, attributes);
}

type LogArgs = Parameters<typeof faro.api.pushLog>[0];
type LogOptions = Parameters<typeof faro.api.pushLog>[1];

/**
 * Sends a log entry to Grafana Faro.
 *
 * @param args - Log payload as an array (first item is usually the message).
 * @param options - Optional Faro log options (for example `level` and `context`).
 *
 * @example
 * addLog(['Signup step started'], {
 *   level: 'info',
 *   context: { flow: 'partners-signup', step: 'starting' },
 * });
 */
export function addLog(args: LogArgs, options?: LogOptions) {
  if (config.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[LOG]: ${JSON.stringify(args)}`, JSON.stringify(options));
    return;
  }
  faro.api.pushLog(args, options);
}
